/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import useFetchList from 'hooks/useFetchList';
import { Box, makeStyles } from '@material-ui/core';
import Modal from '../../../../../Shared/Modal';
import { useSelector } from 'react-redux';
import { ALLOWED_ACCOUNT_COLUMNS } from '../../../../constants';
import { getLocalizedLabel } from '../../../../../../helpers/localize';
import { CUSTOM_TAGS_OPTIONS } from '../../../../../Shared/CustomTags';
import AllowedAccountsModalFooter from './AllowedAccountsModalFooter';
import debounce from 'lodash/debounce';
import QhsTable from '../../../../../Shared/Table/Table';
import { useGenerateColumns } from '../../../../../Shared/Table/helpers';
import { TABLE_KEYS } from '../../../../../../redux_store/reducer/reducers/tableReducer/tableReducer';

const useStyles = makeStyles((theme) => ({
  modal: {
    maxWidth: 1000,
  },
  tableContainer: {
    height: 600,
    width: '100%',
    minWidth: 410,
    maxWidth: 'calc(100vw - 100px)',
    maxHeight: 'calc(100vh - 360px)',
    padding: 10,
    paddingTop: 0,
    borderRadius: 10,
    border: '1px solid rgba(0, 0, 0, 0.07)',
  },
}));


const AllowedAccountsSectionModal = ({
  values,
  initAllowedAccounts,
  changeAllowedAccounts,
  toggleAllowedAccountsModal,
  openAllowedAccountsModal,
}) => {
  const { en: { titles } } = useSelector((state) => state.i18n);
  const classes = useStyles();
  const [uuidsForSending, setUuidsForSending] = useState({
    includedAccountsUUIDs: values?.allowedAccounts ?? [],
    excludedAccountsUUIDs: initAllowedAccounts?.filter(x => !values?.allowedAccounts?.includes(x)) ?? [],
  });
  const [includedAccountsUUIDs, setIncludedAccountsUUIDs] = useState(values?.allowedAccounts ?? []);
  const [excludedAccountsUUIDs, setExcludedAccountsUUIDs] = useState(
    initAllowedAccounts?.filter(x => !values?.allowedAccounts?.includes(x)) ?? []
  );
  const [rowSelection, setRowSelection] = useState({});
  const tableKey = TABLE_KEYS.allowedAccountsTable;
  const columns = useGenerateColumns(ALLOWED_ACCOUNT_COLUMNS, tableKey, {}, {}).columns;
  const fields = columns.map((columnData) => columnData.accessorKey).join();

  const baseEndpoint = `/allowedAccounts/users/${values?.uuid ?? 'empty'}`;
  const {
    items,
    totalCount,
    sort,
    filter,
    loading: dataLoading,
    onLoadMoreRows,
    onSort,
    onFilter,
  } = useFetchList({
    baseEndpoint,
    baseParams: {
      fields: fields,
      q: undefined,
    },
    customMethod: 'POST',
    patches: {
      'includedAccountsUUIDs': uuidsForSending.includedAccountsUUIDs,
      'excludedAccountsUUIDs': uuidsForSending.excludedAccountsUUIDs,
    },
    tableKey,
    canLoad: fields.length,
  });

  useEffect(() => {
    let newSelectedRows = {};
    values?.allowedAccounts.forEach(uuid => newSelectedRows[uuid] = true);
    setRowSelection(newSelectedRows);
  }, []);

  useEffect(() => {
    if (items) {
      let newSelectedRows = {};
      items.filter(row => row.isAllowed).forEach(row => newSelectedRows[row.uuid] = true);
      setRowSelection(newSelectedRows);
    }
  }, [items]);

  const updateUuidsInEndpoint = (f) => (...props) => {
    setUuidsForSending({
      includedAccountsUUIDs,
      excludedAccountsUUIDs,
    });
    f(...props);
  };

  const closeModal = () => {
    toggleAllowedAccountsModal(false);
    setIncludedAccountsUUIDs(values?.allowedAccounts ?? []);
    setExcludedAccountsUUIDs([]);
    if (items && values?.allowedAccounts) {
      let newRowSelection = {};
      items.forEach((account, index) => {
        if (values?.allowedAccounts.includes(account.uuid)) {
          newRowSelection[account.uuid] = true;
        }
      });
      setRowSelection(newRowSelection);
    } else {
      setRowSelection({});
    }
  };

  const accounts = useMemo(
    () => {
      if (items) {
        return items.map((account) => {
          return {
            ...account,
            customTags: account.customTags ? account.customTags.map(item => getLocalizedLabel(
              CUSTOM_TAGS_OPTIONS,
              item,
            )) : [],
            isAllowed: account.isAllowed ? 'Allowed' : 'Not allowed',
          };
        });
      }
    },
    [items, openAllowedAccountsModal],
  );

  const updateSelectedRows = (props) => {
    const newRows = props(rowSelection);
    setRowSelection(newRows);
    const newSelectedItems = Object.keys(newRows).filter((key) => {
      return newRows[key];
    });

    let newIncludedAccounts = accounts.filter(x => newSelectedItems.includes(x.uuid)).map(x => x.uuid);
    let newAllExcludedAccountsInCurrentList = accounts.filter(x => !newSelectedItems.includes(x.uuid)).map(x => x.uuid);
    let newExcludedAccounts = values?.allowedAccounts.filter(x => newAllExcludedAccountsInCurrentList.includes(x));

    setIncludedAccountsUUIDs([...new Set([
      ...includedAccountsUUIDs.filter(x => !newExcludedAccounts.includes(x))
      , ...newIncludedAccounts,
    ])]);
    setExcludedAccountsUUIDs([...new Set([
      ...excludedAccountsUUIDs.filter(x => !newIncludedAccounts.includes(x)),
      ...newExcludedAccounts,
    ])]);
  };

  // prevent loading data after each row click
  const _onLoadMoreRows = updateUuidsInEndpoint(onLoadMoreRows);
  const _onSort = updateUuidsInEndpoint(onSort);
  const _onFilter = debounce(updateUuidsInEndpoint(onFilter), 500);

  return (<>
    <Modal
      open={openAllowedAccountsModal}
      classes={{ paper: classes.modal }}
      title={titles.allowedAccounts}
      handleClose={closeModal}
    >
      <Box p={3} display='flex' flexDirection={'column'}>
        <div className={classes.tableContainer}>
          <QhsTable
            columns={columns}
            data={accounts || []}
            fetchNextPage={_onLoadMoreRows}
            isFetching={dataLoading}
            isLoading={dataLoading && !accounts || !openAllowedAccountsModal}
            totalRowCount={totalCount}
            sorting={sort}
            setSorting={_onSort}
            filter={filter}
            setFilters={_onFilter}
            tableKey={tableKey}
            enableRowSelection={true}
            rowSelection={rowSelection}
            setRowSelection={updateSelectedRows}
          />
        </div>
        <AllowedAccountsModalFooter
          applyChanges={() => {
            changeAllowedAccounts(includedAccountsUUIDs);
            closeModal();
          }}
          closeModal={closeModal}
        />
      </Box>
    </Modal>
  </>
  );
};

export default AllowedAccountsSectionModal;
